import React from 'react';

import { faClock } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { UseFormReturn } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHookFormMask } from 'use-mask-input';

import { CustomAllowedTimeFormSchemaValues } from 'src/features/allowedTimes/utils/formSchema';
import { allowedTimesMessages } from 'src/features/allowedTimes/utils/messages';
import { DAY_OF_WEEK } from 'src/features/membershipType/types';
import { DAY_OF_WEEK_TRANSLATIONS_MAP } from 'src/features/workingHours/utils/constants';
import { workingHoursMessages } from 'src/features/workingHours/utils/messages';
import MultiSelectFormField from 'src/registry/components/forms/MultiSelectFormField';
import { Form, FormControl, FormControlStack, FormField, FormItem, FormLabel, FormMessage } from 'src/registry/ui/Form';
import { Input } from 'src/registry/ui/Input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/registry/ui/Select';

type AllowedTimeFormProps = {
    form: UseFormReturn<CustomAllowedTimeFormSchemaValues>;
    onSubmit: (values: CustomAllowedTimeFormSchemaValues) => void;
    isCreate: boolean;
};

function AllowedTimeForm({ onSubmit, form, isCreate }: AllowedTimeFormProps) {
    const { formatMessage } = useIntl();

    const options = React.useMemo(
        () => [
            {
                id: DAY_OF_WEEK.MONDAY,
                name: formatMessage(workingHoursMessages.monday),
            },
            {
                id: DAY_OF_WEEK.TUESDAY,
                name: formatMessage(workingHoursMessages.tuesday),
            },
            {
                id: DAY_OF_WEEK.WEDNESDAY,
                name: formatMessage(workingHoursMessages.wednesday),
            },
            {
                id: DAY_OF_WEEK.THURSDAY,
                name: formatMessage(workingHoursMessages.thursday),
            },
            {
                id: DAY_OF_WEEK.FRIDAY,
                name: formatMessage(workingHoursMessages.friday),
            },
            {
                id: DAY_OF_WEEK.SATURDAY,
                name: formatMessage(workingHoursMessages.saturday),
            },
            {
                id: DAY_OF_WEEK.SUNDAY,
                name: formatMessage(workingHoursMessages.sunday),
            },
        ],
        [formatMessage],
    );

    const registerWithMask = useHookFormMask(form.register);

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)} variant="dialog">
            {isCreate ? (
                <MultiSelectFormField<CustomAllowedTimeFormSchemaValues>
                    form={form}
                    required
                    name="day_of_week"
                    label={formatMessage(allowedTimesMessages.dayOfWeek)}
                    options={options}
                />
            ) : (
                <FormField
                    control={form.control}
                    name="day_of_week"
                    render={({ field }) => {
                        return (
                            <FormItem variant="dialog">
                                <FormLabel variant="dialog" required>
                                    {formatMessage(allowedTimesMessages.dayOfWeek)}
                                </FormLabel>
                                <FormControlStack>
                                    <FormControl>
                                        <Select
                                            onValueChange={(value) => {
                                                field.onChange([value]);
                                            }}
                                            value={field.value?.toString()}
                                        >
                                            <SelectTrigger className="w-full">
                                                <SelectValue
                                                    placeholder={`${formatMessage(allowedTimesMessages.dayOfWeek)}...`}
                                                />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {Object.values(DAY_OF_WEEK).map((option) => (
                                                    <SelectItem key={option} value={option}>
                                                        {formatMessage(DAY_OF_WEEK_TRANSLATIONS_MAP[option])}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </FormControl>
                                    <FormMessage />
                                </FormControlStack>
                            </FormItem>
                        );
                    }}
                />
            )}

            <FormField
                control={form.control}
                name="from_time"
                render={() => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            <FormattedMessage {...allowedTimesMessages.from} />
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <div className="relative flex min-w-32 items-center gap-2">
                                    <FontAwesomeIcon
                                        icon={faClock}
                                        size="sm"
                                        className="text-muted-foreground pointer-events-none absolute top-1/2 left-2 -translate-y-1/2"
                                    />
                                    <Input
                                        type="text"
                                        placeholder="00-23:59"
                                        inputMode="numeric"
                                        className="pl-8 text-sm"
                                        {...registerWithMask('from_time', 'datetime', {
                                            inputFormat: 'HH:mm',
                                            outputFormat: 'HH:mm',
                                            showMaskOnHover: false,
                                            placeholder: '0',
                                        })}
                                    />
                                </div>
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            <FormField
                control={form.control}
                name="to_time"
                render={() => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            <FormattedMessage {...allowedTimesMessages.to} />
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <div className="relative flex min-w-32 items-center gap-2">
                                    <FontAwesomeIcon
                                        icon={faClock}
                                        size="sm"
                                        className="text-muted-foreground pointer-events-none absolute top-1/2 left-2 -translate-y-1/2"
                                    />
                                    <Input
                                        type="text"
                                        placeholder="00-23:59"
                                        inputMode="numeric"
                                        className="pl-8 text-sm"
                                        {...registerWithMask('to_time', 'datetime', {
                                            inputFormat: 'HH:mm',
                                            outputFormat: 'HH:mm',
                                            showMaskOnHover: false,
                                            placeholder: '0',
                                        })}
                                    />
                                </div>
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
        </Form>
    );
}

export default AllowedTimeForm;
